import { finalizeBasket, selectBasketIsEmpty, selectBasketPaymentInProgress, selectBasketTenantData, updatePaymentInProgress } from 'containers/OrderDetails/basketSlice'
import { getBalance, getFavorites, selectIsLoggedIn } from 'containers/Login/loginSlice'
import { clearPaymentReturn, readPaymentReturn } from 'payments/returnPath'
import { useHistory, useLocation } from 'react-router-dom'
import React, { useEffect, useRef, useState } from 'react'
import { useReduxDispatch, useReduxSelector } from 'store'
import useBasketFinalize from 'hooks/useBasketFinalize'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import BackBtn from 'components/buttons/BackBtn'
import Loading from 'components/Loading'
import { GET } from 'rest/request'
import * as urls from 'rest/urls'
import './style.scss'

interface Response {
  sold: boolean
  contracts?: any[]
  freshUser?: boolean
  orderTitle?: string
  orderNumber?: string
  orderSummary?: string
  paymentStatus: 'INIT' | 'PENDING' | 'SUCCESS' | 'FAILURE'
}

const PaymentStatus: React.FC = () => {
  const [paymentReturn] = useState(readPaymentReturn)
  const methodReturnPath = paymentReturn?.kind === 'paymentMethod' ? paymentReturn.path : ''
  const invoiceReturnPath = paymentReturn?.kind === 'invoice' ? paymentReturn.path : ''
  const isLoggedIn = useReduxSelector(selectIsLoggedIn)
  const tenant = useReduxSelector(selectBasketTenantData)
  const isBasketEmpty = useReduxSelector(selectBasketIsEmpty)
  const paymentInProgress = useReduxSelector(selectBasketPaymentInProgress)
  const [data, setData] = useState<Response | null>(null)
  const { search } = useLocation()

  const currentData = useRef({
    isLoggedIn,
    paymentInProgress,
    tenant
  })
  currentData.current.tenant = tenant
  currentData.current.isLoggedIn = isLoggedIn
  currentData.current.paymentInProgress = paymentInProgress

  const { replace } = useHistory()
  const dispatch = useReduxDispatch()
  const { t } = useTranslation('payment')
  const { onBoardUser } = useBasketFinalize()

  useEffect(() => {
    let timeout: basic.SetTimeout
    let isActive = true

    const getStatus = async () => {
      const response: Response = await GET(`${urls.paymentCompletedStatus}${search}`)
      if (response) {
        if (response.paymentStatus === 'SUCCESS' || response.paymentStatus === 'FAILURE') {
          if (currentData.current.paymentInProgress && response.paymentStatus === 'SUCCESS') {
            dispatch(finalizeBasket(response.contracts || []))
            if (currentData.current.tenant?.user?.id) {
              onBoardUser(currentData.current.tenant.user.id)

            } else if (currentData.current.isLoggedIn) {
              dispatch(getFavorites())
              replace('/newwarehouse')

            } else {
              replace('/login')
            }

          } else {
            if (currentData.current.paymentInProgress) {
              dispatch(updatePaymentInProgress(false))
            } else if (currentData.current.isLoggedIn) {
              dispatch(getBalance())
            }
            setData(response)
          }
        } else if (isActive) {
          timeout = setTimeout(getStatus, 3_000)
        }
      }
    }

    if (search) {
      getStatus()
      return () => {
        isActive = false
        if (timeout) {
          clearTimeout(timeout)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, replace, search])

  useEffect(() => {
    if (data) clearPaymentReturn()
  }, [data])

  return (
    <div className='paymentStatus'>
      <div className='title'>
        <div>{t('paymentStatus')}</div>
        {data
          ? <div>{t(data.paymentStatus === 'SUCCESS' ? 'paymentSuccessful' : 'paymentFailed')}!</div>
          : <div>{t('waitingForPaymentStatus')}</div>}
      </div>

      {data && methodReturnPath
        ? <div className='paymentStatus__main__wrapper'>
          <div className='paymentStatus__main'>
            <div className='paymentStatus__main__title'>
              {t(data.paymentStatus === 'SUCCESS' ? 'paymentMethodSaved' : 'paymentMethodNotSaved')}
            </div>
            <p>{t(data.paymentStatus === 'SUCCESS' ? 'paymentMethodSavedDesc' : 'paymentMethodNotSavedDesc')}</p>
            <ArrowBtn
              enlarged
              text={t('backToPaymentMethods')}
              action={() => {
                replace(methodReturnPath)
              }} />
          </div>
        </div>
        : data
        ? <div className='paymentStatus__main__wrapper'>
          <div className='paymentStatus__main'>
            <div className='paymentStatus__main__title'>
              {data?.orderTitle || 'Numer zamówienia'}{data?.orderNumber ? <>: <b>{data.orderNumber}</b></> : ''}
            </div>
            {data?.orderSummary
              ? <div className='paymentStatus__main__description' dangerouslySetInnerHTML={{ __html: data.orderSummary }} />
              : <p>{t('pleaseCheckYourEmailAndContactUsIfNeeded')}</p>}
            {!isBasketEmpty && data.paymentStatus === 'FAILURE'
              ? <div className='paymentStatus__main__buttons'>
                <ArrowBtn
                  enlarged
                  text={t('retryPayment')}
                  action={() => {
                    replace('/order/payment')
                  }} />
                <BackBtn
                  text={t('homePage')}
                  action={() => {
                    replace('/')
                  }}
                />
              </div>
              : invoiceReturnPath
                ? <div className='paymentStatus__main__buttons'>
                  <ArrowBtn
                    enlarged
                    text={t(data.paymentStatus === 'FAILURE' ? 'retryPayment' : 'backToInvoices')}
                    action={() => {
                      replace(invoiceReturnPath)
                    }} />
                  <BackBtn
                    text={t('homePage')}
                    action={() => {
                      replace('/')
                    }}
                  />
                </div>
                : <ArrowBtn
                  enlarged
                  text={t('homePage')}
                  action={() => {
                    replace('/')
                  }} />}
          </div>
        </div>
        : <Loading middleOfPage />}
    </div>
  )
}

export default PaymentStatus